import WSGCheckbox from "../../components/Atomos/Checkbox/WSGCheckBox";
import RadioButton from "../../components/Atomos/Radio/RadioButton";
import { useCallback, useMemo } from "react";

import { UseRegionList } from "../../hooks/Catalogs/useRegionListOptions";
import { StatusEnum, StatusType } from "../../Enums/EventEnums";
import { useEventForm } from "../../hooks/Events/useEventForm";
import { UseGroupList } from "../../hooks/Catalogs/useGroupList";
import { GROUP_TYPES } from "../../Enums/GroupEnum";
import { IGroup } from "../../interfaces/models/IGroup";

const AdditionalSettings = () => {
  const { regionData } = UseRegionList();
  const { groupData } = UseGroupList();
  const availableStatus = Object.entries(StatusEnum) as [StatusType, string][];
  const { event, updateEventField } = useEventForm();

  const filterUniqueGroupsByName = useCallback((groups: IGroup[]): IGroup[] => {
    const seen = new Set<string>();
    const uniqueGroups = groups.filter((group) => {
      const title = group.title?.trim().toLowerCase() || "";
      if (seen.has(title)) {
        return false;
      }
      seen.add(title);
      return true;
    });
    
    return uniqueGroups.sort((a, b) => {
      const titleA = (a.title || "").toLowerCase();
      const titleB = (b.title || "").toLowerCase();
      return titleA.localeCompare(titleB);
    });
  }, []);

  const specialityGroupsFiltered = useMemo(() => {
    const filtered = groupData.filter(
      (group) =>
        !group.globalGroupId &&
        (group.type === GROUP_TYPES.ASSOCIATION || group.type === GROUP_TYPES.WSGOPEN)
    );
    return filterUniqueGroupsByName(filtered);
  }, [groupData, filterUniqueGroupsByName]);

  const practiceGroupsFiltered = useMemo(() => {
    const filtered = groupData.filter(
      (group) => !group.globalGroupId && group.type === GROUP_TYPES.PRACTICE
    );
    return filterUniqueGroupsByName(filtered);
  }, [groupData, filterUniqueGroupsByName]);

  const industryGroupsFiltered = useMemo(() => {
    const filtered = groupData.filter(
      (group) => !group.globalGroupId && group.type === GROUP_TYPES.INDUSTRY
    );
    return filterUniqueGroupsByName(filtered);
  }, [groupData, filterUniqueGroupsByName]);

  const isGroupSelected = useCallback(
    (groupTitle: string): boolean => {
      const groupIds = event.groupIds || [];
      return groupIds.includes(groupTitle);
    },
    [event.groupIds]
  );

  const handleGroupToggle = useCallback(
    (groupTitle: string) => {
      const currentGroupIds = event.groupIds || [];
      const isSelected = currentGroupIds.includes(groupTitle);

      if (isSelected) {
        const updatedGroupIds = currentGroupIds.filter((id) => id !== groupTitle);
        updateEventField("groupIds", updatedGroupIds);
      } else {
        if (!currentGroupIds.includes(groupTitle)) {
          updateEventField("groupIds", [...currentGroupIds, groupTitle]);
        }
      }
    },
    [event.groupIds, updateEventField]
  );

  const isRegionSelected = useCallback(
    (regionId: string): boolean => {
      const regionIds = event.regionIds || [];
      return regionIds.includes(regionId);
    },
    [event.regionIds]
  );

  const handleRegionToggle = useCallback(
    (regionId: string) => {
      const currentRegionIds = event.regionIds || [];
      const isSelected = currentRegionIds.includes(regionId);

      if (isSelected) {
        const updatedRegionIds = currentRegionIds.filter((id) => id !== regionId);
        updateEventField("regionIds", updatedRegionIds);
      } else {
        if (!currentRegionIds.includes(regionId)) {
          updateEventField("regionIds", [...currentRegionIds, regionId]);
        }
      }
    },
    [event.regionIds, updateEventField]
  );


  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-5">
        <div className="text-secondary text-xl md:text-2xl font-decimal w-full">
          Event Status
        </div>
        <div className="flex flex-col md:flex-row md gap-4 md:gap-6 lg:gap-8">
          {availableStatus.map(([key, value], index) => (
            <RadioButton
              key={key}
              index={index}
              text={value}
              selected={event.status === key}
              handleRadioButtonChange={() => updateEventField("status", key)}
              fontSize="!text-base md:!text-lg"
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-5">
        <div className="text-secondary text-xl md:text-2xl font-decimal w-full">
          Visible
        </div>
        <div className="grid grid-cols-12">
          <div className="col-span-12 lg:col-span-2">
            <WSGCheckbox
              index={0}
              text="Home Page"
              checked={event.homePage ?? false}
              handleCheckboxChange={() =>
                updateEventField("homePage", !event.homePage)
              }
            />
          </div>
          <div className="col-span-12 lg:col-span-3">
            <WSGCheckbox
              index={1}
              text="Upcoming Events Page"
              checked={event.upcomingEventPage ?? false}
              handleCheckboxChange={() =>
                updateEventField("upcomingEventPage", !event.upcomingEventPage)
              }
            />
          </div>
          <div className="col-span-12 lg:col-span-3">
            <WSGCheckbox
              index={2}
              text="Member events tab"
              checked={event.memberEventsTab ?? false}
              handleCheckboxChange={() =>
                updateEventField("memberEventsTab", !event.memberEventsTab)
              }
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-5">
        <div className="text-secondary text-xl md:text-2xl font-decimal w-full">
          Regions
        </div>
        <div className="grid grid-cols-12">
          {regionData.map((region, index) => (
            <div
              className="col-span-12 md:col-span-6 lg:col-span-2"
              key={region.id || `region-${index}`}
            >
              <WSGCheckbox
                index={index}
                text={region.name}
                checked={isRegionSelected(region.id || "")}
                handleCheckboxChange={() => handleRegionToggle(region.id || "")}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-5">
        <div className="text-secondary text-xl md:text-2xl font-decimal w-full">
          Practice Groups
        </div>
        <div className="grid grid-cols-12">
          {practiceGroupsFiltered.map((group, index) => (
            <div
              className="col-span-12 md:col-span-6 lg:col-span-4"
              key={group.id || `practice-${index}`}
            >
              <WSGCheckbox
                index={index}
                text={group.title}
                checked={isGroupSelected(group.title)}
                handleCheckboxChange={() => handleGroupToggle(group.title)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-5">
        <div className="text-secondary text-xl md:text-2xl font-decimal w-full">
          Industry Groups
        </div>
        <div className="grid grid-cols-12">
          {industryGroupsFiltered.map((group, index) => (
            <div
              className="col-span-12 md:col-span-6 lg:col-span-4"
              key={group.id || `industry-${index}`}
            >
              <WSGCheckbox
                index={index}
                text={group.title}
                checked={isGroupSelected(group.title)}
                handleCheckboxChange={() => handleGroupToggle(group.title)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-5">
        <div className="text-secondary text-xl md:text-2xl font-decimal w-full">
          Specialty Groups
        </div>
        <div className="grid grid-cols-12">
          {specialityGroupsFiltered.map((group, index) => (
            <div
              className="col-span-12 md:col-span-6 lg:col-span-4"
              key={group.id || `specialty-${index}`}
            >
              <WSGCheckbox
                index={index}
                text={group.title}
                checked={isGroupSelected(group.title)}
                handleCheckboxChange={() => handleGroupToggle(group.title)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdditionalSettings;
